<template>
  <section class="py-12">
    <div class="container-wrap">
      <h2 class="mb-2 text-2xl font-bold desktop:text-3xl">
        {{ t('title') }}
      </h2>
      <p class="mb-6 text-sm text-gray-600 tablet:text-base">
        {{ t('description') }}
        <TheNuxtLink
          :to="contextViewAllUrl"
          :title="t('view-all')"
          class="font-bold text-blue-400 underline hover:text-blue-500"
        >
          {{ t('view-all') }}
        </TheNuxtLink>
      </p>
      <div>
        <ul
          class="scrollbar-h-px extend-width mb-6 flex w-full overflow-y-hidden overflow-x-scroll desktop:grid desktop:grid-cols-4 desktop:gap-3 desktop:overflow-x-hidden"
        >
          <li
            v-for="(item, index) in contextCategories"
            :key="item"
            class="mr-2 desktop:mr-0 desktop:w-full desktop:border-b-4 desktop:border-solid"
            :class="provideClass(index)"
          >
            <button
              class="w-full rounded-3xl border border-solid border-gray-400 px-4 py-1.5 desktop:flex desktop:items-center desktop:rounded desktop:border-none desktop:px-2 desktop:py-3 desktop:hover:bg-blue-400/10 desktop:hover:text-blue-400"
              :class="
                current === index
                  ? 'bg-blue-400 text-white desktop:bg-blue-400/10 desktop:text-blue-400'
                  : 'bg-white text-blue-400 desktop:text-black'
              "
              :title="$t(`menu.product.category.${item}`)"
              @click="current = index"
            >
              <IconCommSolution v-if="isDesktop" class="h-6 w-6 basis-6" />
              <span
                class="truncate text-left text-sm font-bold desktop:flex-1 desktop:pl-2 desktop:text-base"
              >
                {{ $t(`menu.product.category.${item}`) }}
              </span>
            </button>
          </li>
        </ul>
        <ul class="flex flex-wrap tablet:gap-5">
          <li
            v-for="item in contextProducts"
            :key="item.alias"
            class="mb-3 w-full tablet:mb-0 tablet:w-[calc(50%-16px)] desktop:min-h-[356px] desktop:w-[calc(25%-16px)]"
          >
            <component
              :is="isMobile ? TheNuxtLink : 'div'"
              v-bind="isMobile ? { to: provideProductUrl(item.alias) } : {}"
              class="flex h-full w-full flex-col justify-between rounded bg-white p-3 shadow-normal shadow-gray-400 tablet:p-5"
            >
              <div>
                <div class="flex items-center tablet:block">
                  <figure
                    class="mb-2 h-12 w-12 basis-12 overflow-hidden rounded-lg shadow-normal shadow-gray-400 tablet:h-20 tablet:w-20 tablet:basis-20 tablet:rounded-2xl"
                  >
                    <img
                      :src="item.icon"
                      :alt="item.name"
                      loading="lazy"
                      class="pointer-events-none h-full w-full object-cover"
                    />
                  </figure>
                  <strong
                    class="mb-2 line-clamp-2 max-h-12 flex-1 pl-3 text-base tablet:pl-0"
                  >
                    {{ item.name }}
                  </strong>
                </div>
                <p
                  class="mb-2 text-sm text-gray-600 tablet:line-clamp-4 tablet:h-[80px] desktop:line-clamp-6 desktop:h-[120px]"
                >
                  {{ item.description }}
                </p>
              </div>
              <div>
                <div v-if="isDesktop" class="flex flex-wrap">
                  <span
                    v-for="tagItem in item.tags.slice(0, 2)"
                    :key="hash(tagItem)"
                    class="mb-2 mr-2 inline-block rounded-xl border border-solid border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600"
                  >
                    {{ tagItem }}
                  </span>
                </div>
                <div v-if="!isMobile" class="flex justify-end">
                  <TheArrowLink
                    :to="provideProductUrl(item.alias)"
                    :title="item.name"
                  />
                </div>
              </div>
            </component>
          </li>
        </ul>
      </div>
      <div class="flex justify-center pt-8 desktop:pb-8">
        <button
          :title="t('see-more')"
          class="flex items-center font-bold text-blue-400 hover:text-blue-500"
          @click="handleClickSeeMore"
        >
          <span class="mr-1">{{ t('see-more') }}</span>
          <IconCaretDouble direction="right" />
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import hash from 'object-hash';

  import type {
    IHomeCategory,
    IHomeProduct,
  } from '@/assets/types/api/Categories';

  const TheNuxtLink = resolveComponent('TheNuxtLink');

  const router = useRouter();
  const { t, locale } = useI18n({ useScope: 'local' });
  const { isCN } = useLanguage();
  const { isMobile, isTablet, isDesktop } = useScreen();

  const homeStore = useHomeStore();
  const { products } = storeToRefs(homeStore);

  const current = ref<number>(0);
  const carouselTimer = ref<NodeJS.Timeout | null>(null);

  const contextCategories = computed((): string[] => {
    return products.value.map((domain: IHomeCategory) => domain.code);
  });

  const contextProducts = computed((): IHomeProduct[] => {
    return products.value[current.value]?.products || [];
  });

  const contextViewAllUrl = computed((): string =>
    isCN.value
      ? `/${EnumLocale.ZH_CN}/marketplace/category/featured-products`
      : `/${locale.value}/category/featured-products`
  );

  const contextSeeMoreUrl = computed((): string => {
    return provideCategoryUrl(products.value[current.value]?.code || '');
  });

  const removeCarouselTimer = (): void => {
    current.value = 0;
    if (carouselTimer.value) {
      clearInterval(carouselTimer.value);
      carouselTimer.value = null;
    }
  };

  const setCarouselTimer = (): void => {
    if (carouselTimer.value) {
      clearInterval(carouselTimer.value);
    }

    carouselTimer.value = setInterval(() => {
      if (current.value === products.value.length - 1) {
        current.value = 0;
      } else {
        current.value += 1;
      }
    }, 10000);
  };

  const provideClass = (index: number): string => {
    const widthClass: string =
      locale.value === EnumLocale.EN_EMEA
        ? 'desktop:w-[calc(33%-16px)]'
        : 'desktop:w-[calc(25%-16px)]';

    const borderClass: string =
      current.value === index
        ? 'desktop:border-blue-400'
        : 'desktop:border-[transparent]';

    return `${widthClass} ${borderClass}`;
  };

  const provideProductUrl = (productAlias: string): string => {
    return `/${locale.value}/marketplace/products/${productAlias}`;
  };

  const provideCategoryUrl = (categoryCode: string): string => {
    return `/${locale.value}/marketplace/category/${categoryCode}`;
  };

  const handleClickSeeMore = (): void => {
    router.push(contextSeeMoreUrl.value);
  };

  watch(isDesktop, (value: boolean) => {
    if (value) {
      removeCarouselTimer();
      setCarouselTimer();
    }
  });

  watch(isTablet, (value: boolean) => {
    if (value) {
      removeCarouselTimer();
    }
  });

  watch(isMobile, (value: boolean) => {
    if (value) {
      removeCarouselTimer();
    }
  });

  onMounted(() => {
    if (isDesktop.value) {
      setCarouselTimer();
    }
  });

  onBeforeUnmount(() => {
    if (carouselTimer.value) {
      clearInterval(carouselTimer.value);
      carouselTimer.value = null;
    }
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Explore Products by Capability",
    "description": "Discover ready-to-use products to support deployment and scale industrial AI applications.",
    "view-all": "View all products",
    "see-more": "See more"
  },
  "en-jp": {
    "title": "Explore Products by Capability",
    "description": "Discover ready-to-use products to support deployment and scale industrial AI applications.",
    "view-all": "View all products",
    "see-more": "See more"
  },
  "en-tw": {
    "title": "Explore Products by Capability",
    "description": "Discover ready-to-use products to support deployment and scale industrial AI applications.",
    "view-all": "View all products",
    "see-more": "See more"
  },
  "en-kr": {
    "title": "Explore Products by Capability",
    "description": "Discover ready-to-use products to support deployment and scale industrial AI applications.",
    "view-all": "View all products",
    "see-more": "See more"
  },
  "en-americas": {
    "title": "Explore Products by Capability",
    "description": "Discover ready-to-use products to support deployment and scale industrial AI applications.",
    "view-all": "View all products",
    "see-more": "See more"
  },
  "en-asia-pacific": {
    "title": "Explore Products by Capability",
    "description": "Discover ready-to-use products to support deployment and scale industrial AI applications.",
    "view-all": "View all products",
    "see-more": "See more"
  },
  "en-emea": {
    "title": "Explore Products by Capability",
    "description": "Discover ready-to-use products to support deployment and scale industrial AI applications.",
    "view-all": "View all products",
    "see-more": "See more"
  },
  "ja-jp": {
    "title": "機能別製品を探す",
    "description": "産業用AIアプリケーションの導入と拡張を支援する、すぐに使える製品をご覧ください。",
    "view-all": "すべての製品を見る",
    "see-more": "もっと見る"
  },
  "zh-cn": {
    "title": "以特定领域的应用服务成就转型",
    "description": "通过研华工业云应用市集精选产品，轻松迈出第一步。",
    "view-all": "查看所有产品",
    "see-more": "了解更多"
  },
  "zh-tw": {
    "title": "依功能探索產品",
    "description": "探索可立即使用的產品，以支援工業人工智慧應用的部署和擴展。",
    "view-all": "查看所有產品",
    "see-more": "查看更多"
  }
}
</i18n>
